/**
 * Le verbe de lecture de l'encadrement de chantier.
 *
 * Un seul service, N1 : l'écran du chef ouvre son chantier d'un coup, et les
 * trois lectures partent ensemble. L'ouvrier est refusé ici, proprement ; la
 * base le refuse de toute façon, et borne le chef à ses chantiers.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import { SITE_LEAD_ROLES, groupScheduleByDay } from "./types.js";
import type { SiteLeadView, SiteReport, SiteScheduleEntry, SiteTeamMember } from "./types.js";

export const readSiteLeadInput = z.object({
  confirmationId: z.string().optional(),
  projectId: z.string().uuid("Chantier attendu"),
  /** Combien de jours de planning à partir de demain. La base borne à un mois. */
  days: z.number().int().min(1).max(31).optional(),
});

export type ReadSiteLeadInput = z.infer<typeof readSiteLeadInput>;

/** Une semaine : ce que le chef regarde le vendredi pour lundi. */
export const SITE_SCHEDULE_DEFAULT_DAYS = 7;

export interface SiteLeadRepository {
  team(ctx: ExecutionContext, projectId: string): Promise<SiteTeamMember[]>;
  schedule(ctx: ExecutionContext, projectId: string, days: number): Promise<SiteScheduleEntry[]>;
  reports(ctx: ExecutionContext, projectId: string): Promise<SiteReport[]>;
}

export function createSiteLeadServices(repo: SiteLeadRepository) {
  const lireEncadrementChantier = defineService<ReadSiteLeadInput, SiteLeadView>({
    name: "lire_encadrement_chantier",
    level: ActionLevel.Read,
    allowedRoles: SITE_LEAD_ROLES,
    handler: async (ctx, input) => {
      const [team, schedule, reports] = await Promise.all([
        repo.team(ctx, input.projectId),
        repo.schedule(ctx, input.projectId, input.days ?? SITE_SCHEDULE_DEFAULT_DAYS),
        repo.reports(ctx, input.projectId),
      ]);
      return { team, schedule: groupScheduleByDay(schedule), reports };
    },
  });

  return { lireEncadrementChantier };
}

export type SiteLeadServices = ReturnType<typeof createSiteLeadServices>;
